import type { Observable } from '@apollo/client';
import { fromPromise } from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';

import { refreshToken } from '@/entities/authentication';
import { getAccessToken } from '@/shared/utils/token.utils';
import { client } from './index';

type CallbackFunction = (value?: unknown) => void;

let isRefreshing = false;
let pendingRequests: CallbackFunction[] = [];

const resolvePendingRequests = () => {
  pendingRequests.forEach(callback => callback());
  pendingRequests = [];
};

const setAuthHeader = (operation: ErrorResponse['operation']) => {
  const accessToken = getAccessToken();
  const { headers } = operation.getContext();
  operation.setContext({
    headers: {
      ...headers,
      ...(accessToken && { authorization: `Bearer ${accessToken}` }),
    },
  });
};

export const handleRefreshToken = ({
  forward,
  operation,
}: ErrorResponse): Observable<unknown> | void => {
  if (!isRefreshing) {
    isRefreshing = true;

    return fromPromise(
      refreshToken(client)
        .then(res => {
          if (res?.accessToken) {
            resolvePendingRequests();
            return true;
          }
          throw new Error('Error while handling refresh token');
        })
        .catch(error => {
          resolvePendingRequests();
          throw error;
        })
        .finally(() => {
          isRefreshing = false;
        }),
    ).flatMap(() => {
      setAuthHeader(operation);
      return forward(operation) as Observable<unknown>;
    });
  }

  // Queue this operation to retry after refresh completes
  return fromPromise(
    new Promise<void>(resolve => {
      pendingRequests.push(() => resolve());
    }),
  ).flatMap(() => {
    setAuthHeader(operation);
    return forward(operation) as Observable<unknown>;
  });
};
